import React from 'react'
import { Row, Col } from 'react-bootstrap'
import { Model } from '../../../types/models/model'
import { rateBBoxModel, rateClassificationModel } from '../../../utils'
import { useAppSelector } from '../../../store/hooks'
import { useGetModelQuery } from '../../../services/modelsApi'
import { selectSelectedModelId } from '../../../store/slices/modelSlice'
import PerformanceCard from './components/PerformanceCard'
import DataSourcesCard from './components/DataSourcesCard'
import { selectFilterTags } from '../../../store/slices/projectSlice'
import BBoxMetricsCard from './components/BBoxMetricsCard'
import DistributionCard from './components/DistributionCard'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { useGetProjectQuery } from '../../../services/projectsApi'
import ClassificationMetricsCard from './components/ClassificationMetricsCard'
import type { Tag } from '../../../types/models/tag'

interface ModelPerformanceCardProps {
  onDeleteModel?: () => void
  onEditModel?: () => void
  isEditingName?: boolean
  modelName?: string
  onNameChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onNameBlur?: () => void
}

const ModelPerformanceCard: React.FC<ModelPerformanceCardProps> = () => {
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const selectedModelId = useAppSelector(selectSelectedModelId)
  const { data: model, isLoading: isModelLoading } = useGetModelQuery(selectedModelId || '', {
    skip: !selectedModelId,
  })
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const selectedFilterTags = useAppSelector(selectFilterTags) as Tag[]
  const selectedTagNames = (selectedFilterTags || []).map((t) => t.name)

  const trainingDataSources = model?.parameters?.train_metadata?.datasource || []
  const validationDataSources = model?.parameters?.validation_metadata?.datasource || []

  const { metrics, train_started_at, train_ended_at, deployment } = model || {}

  const trainStart = train_started_at ? new Date(train_started_at) : null
  const trainEnd = train_ended_at ? new Date(train_ended_at) : null

  const { bBoxRating } = rateBBoxModel(model || ({} as Model))
  const { classificationRating } = rateClassificationModel(model || ({} as Model))

  console.log('model state:', model?.state, 'modelId:', selectedModelId)

  let trainTime = 'N/A'
  if (trainEnd && trainStart) {
    if (train_ended_at === '0001-01-01T00:00:00Z') {
      trainTime = 'In Progress'
    } else {
      const durationMs = trainEnd.getTime() - trainStart.getTime()
      const hours = Math.floor(durationMs / (1000 * 60 * 60))
      const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60))
      trainTime = `${hours}h ${minutes}m`
    }
  }

  const allowedMetrics = ['validation:mAP50-95', 'validation:precision', 'validation:recall']

  let recall = 0
  let precision = 0
  const validationMetrics = Object.entries(metrics || {})
    .filter(([key]) => allowedMetrics.includes(key) && key.startsWith('validation:'))
    .map(([key, value]) => {
      const percentageValue = typeof value === 'number' ? value * 100 : 0
      if (key.includes('precision')) {
        precision = typeof value === 'number' ? value * 100 : 0
      }
      if (key.includes('recall')) {
        recall = typeof value === 'number' ? value * 100 : 0
      }
      return { key, value: percentageValue }
    })

  const f1Score = (2 * (precision * recall)) / (precision + recall) || 0
  const combinedMetrics = [...validationMetrics, { key: 'calculated:f1', value: f1Score }]

  const gradients = React.useMemo(
    () => ({
      performance: 'linear-gradient(135deg, #402563, #114ee0)',
      metrics: 'linear-gradient(135deg, #402563, #2042ba)',
      distribution: 'linear-gradient(135deg, #402563, #2448cc)',
    }),
    []
  )

  if (!model) {
    return <div>No model data available.</div>
  }

  return (
    <div className="model-metrics">
      {project?.annotation_type === 'bounding_box' ||
      project?.annotation_type === 'oriented_bounding_box' ? (
        <Row className="g-4">
          <Col lg={4}>
            <div>
              <PerformanceCard
                gradients={gradients}
                bBoxRating={bBoxRating}
                modelState={model.state}
                trainTime={trainTime}
                deploymentStatus={deployment?.status || 'Not Deployed'}
              />
              <DataSourcesCard
                gradients={gradients}
                trainingDataSources={trainingDataSources}
                validationDataSources={validationDataSources}
                trainingTagNames={selectedTagNames}
                validationTagNames={selectedTagNames}
              />
            </div>
          </Col>
          <Col lg={8}>
            <BBoxMetricsCard gradients={gradients} metrics={combinedMetrics} />
          </Col>
        </Row>
      ) : (
        <Row className="g-4">
          <Col lg={4}>
            <PerformanceCard
              gradients={gradients}
              bBoxRating={classificationRating}
              modelState={model.state}
              trainTime={trainTime}
              deploymentStatus={deployment?.status || 'Not Deployed'}
            />
          </Col>
        </Row>
      )}
      {project?.annotation_type === 'classification' && (
        <Row className="g-4">
          <Col lg={4}>
            <PerformanceCard
              gradients={gradients}
              bBoxRating={classificationRating}
              modelState={model.state}
              trainTime={trainTime}
              deploymentStatus={deployment?.status || 'Not Deployed'}
            />
          </Col>
          <Col lg={4}>
            <DataSourcesCard
              gradients={gradients}
              trainingDataSources={trainingDataSources}
              validationDataSources={validationDataSources}
            />
          </Col>
          <Col lg={4}>
            {project?.annotation_type === 'classification' && (
              <ClassificationMetricsCard modelId={model.id} />
            )}
          </Col>
        </Row>
      )}

      <Row className="g-4 mt-0">
        <Col xs={12} lg={6}>
          <DistributionCard
            gradients={gradients}
            title="Training"
            dataSources={trainingDataSources}
            datasetId={model.datasetid}
            isLoading={isModelLoading}
          />
        </Col>
        <Col xs={12} lg={6}>
          <DistributionCard
            gradients={gradients}
            title="Validation"
            dataSources={validationDataSources}
            datasetId={model.datasetid}
            isLoading={isModelLoading}
          />
        </Col>
      </Row>
    </div>
  )
}

export default ModelPerformanceCard
